import api from '../../../lib/api'
import type { FraudAlert, FraudAlertStats, CashReconciliation } from '../../../types'

export interface NegativeConfirmation {
  id: number
  lead_id: number
  sent_via: string
  sent_at: string
  customer_response: string | null
  responded_at: string | null
  is_covered: boolean
  investigation_status: string | null
  resolved_at: string | null
  resolution_note: string | null
  customer_name: string | null
  vehicle_reg_no: string | null
  telecaller_name: string | null
}

// Terminal investigation_status values — once in one of these, the dispute
// is closed and no longer needs action or dashboard attention.
const NEGCONF_TERMINAL_STATUSES = ['RESOLVED', 'FRAUD_ESTABLISHED']

export function isNegConfTerminal(status: string | null | undefined): boolean {
  return !!status && NEGCONF_TERMINAL_STATUSES.includes(status)
}

export const fraudService = {
  async listAlerts(params?: {
    status?: string
    alert_type?: string
    severity?: string
    page?: number
    page_size?: number
  }): Promise<FraudAlert[]> {
    const res = await api.get('/fraud/alerts', { params })
    return res.data
  },

  async getAlertStats(): Promise<FraudAlertStats> {
    const res = await api.get('/fraud/alerts/stats')
    return res.data
  },

  async investigateAlert(alertId: number): Promise<FraudAlert> {
    const res = await api.post(`/fraud/alerts/${alertId}/investigate`)
    return res.data
  },

  async resolveAlert(
    alertId: number,
    note: string,
    markFalsePositive = false,
  ): Promise<FraudAlert> {
    const res = await api.post(`/fraud/alerts/${alertId}/resolve`, null, {
      params: { resolution_notes: note, mark_false_positive: markFalsePositive },
    })
    return res.data
  },

  async listCashReconciliation(): Promise<CashReconciliation[]> {
    const res = await api.get('/fraud/cash-reconciliation')
    return res.data
  },

  async submitCashReconciliation(data: {
    total_cash_deposited: number
    variance_explanation?: string
  }): Promise<CashReconciliation> {
    const res = await api.post('/fraud/cash-reconciliation', data)
    return res.data
  },

  async triggerAnomalyChecks() {
    const res = await api.post('/fraud/run-anomaly-checks')
    return res.data
  },

  async listNegativeConfirmations(): Promise<NegativeConfirmation[]> {
    const res = await api.get('/fraud/negative-confirmations')
    return res.data
  },

  async investigateNegConf(id: number): Promise<NegativeConfirmation> {
    const res = await api.post(`/fraud/negative-confirmations/${id}/investigate`)
    return res.data
  },

  async resolveNegConf(id: number, resolutionNote: string): Promise<NegativeConfirmation> {
    const res = await api.post(`/fraud/negative-confirmations/${id}/resolve`, { resolution_note: resolutionNote })
    return res.data
  },

  async markFraudNegConf(id: number, resolutionNote: string): Promise<NegativeConfirmation> {
    const res = await api.post(`/fraud/negative-confirmations/${id}/mark-fraud`, { resolution_note: resolutionNote })
    return res.data
  },

  async runSequenceAudit(): Promise<{ status: string; alerts_created: number }> {
    const res = await api.post('/fraud/run-sequence-audit')
    return res.data
  },
}

export const callApprovalService = {
  async approveDiscount(callId: number): Promise<unknown> {
    const res = await api.post(`/calls/${callId}/approve-discount`)
    return res.data
  },

  async rejectDiscount(callId: number, reason?: string): Promise<unknown> {
    const res = await api.post(`/calls/${callId}/reject-discount`, null, {
      params: reason ? { reason } : undefined,
    })
    return res.data
  },
}
